import { useState, useEffect, useRef } from 'react';
import blockchainDataManager from '../services/BlockchainDataManager';

/**
 * Custom hook for fetching and managing draw offers
 */
const useDrawOffers = (userAddress, gameInstances = []) => {
  const [drawOffers, setDrawOffers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const subscriptionsRef = useRef([]);

  // Convert array of draw offers to object keyed by offerId for easier lookup
  const formatDrawOffers = (drawOffersArray) => {
    const formatted = {};
    drawOffersArray.forEach(drawOffer => {
      formatted[drawOffer.offerId] = drawOffer;
    });
    return formatted;
  };

  const fetchDrawOffers = async () => {
    if (!userAddress || !blockchainDataManager.isInitialized) {
      setDrawOffers({});
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const drawOffersData = await blockchainDataManager.getData('drawOffers', {
        userAddress,
        activeOnly: true
      });

      const formattedDrawOffers = formatDrawOffers(drawOffersData || []);
      setDrawOffers(formattedDrawOffers);
    } catch (err) {
      console.error('useDrawOffers: Error fetching draw offers:', err);
      setError(err.message);
      setDrawOffers({});
    } finally {
      setLoading(false);
    }
  };

  // Setup subscriptions and initial fetch
  useEffect(() => {
    // Clear existing subscriptions
    subscriptionsRef.current.forEach(unsubscribe => unsubscribe());
    subscriptionsRef.current = [];

    if (!userAddress) {
      setDrawOffers({});
      return;
    }

    // Subscribe to draw offer events
    const unsubscribe = blockchainDataManager.subscribe('drawOffersUpdated', () => {
      console.log('📡 useDrawOffers: Received drawOffersUpdated event, refreshing data');
      fetchDrawOffers();
    });

    subscriptionsRef.current.push(unsubscribe);

    // Subscribe to initialization events
    const initUnsubscribe = blockchainDataManager.subscribe('initialized', () => {
      console.log('📡 useDrawOffers: BlockchainDataManager initialized, fetching initial data');
      fetchDrawOffers();
    });

    subscriptionsRef.current.push(initUnsubscribe);

    // Fetch initial data if manager is already initialized
    if (blockchainDataManager.isInitialized) {
      fetchDrawOffers();
    }

    // Cleanup subscriptions on unmount
    return () => {
      subscriptionsRef.current.forEach(unsubscribe => unsubscribe());
      subscriptionsRef.current = [];
    };
  }, [userAddress]);

  // Refresh draw offers when game instances change (new games might have offers)
  useEffect(() => {
    if (userAddress && blockchainDataManager.isInitialized && gameInstances.length > 0) {
      fetchDrawOffers();
    }
  }, [gameInstances.length, userAddress]);

  return {
    drawOffers,
    loading,
    error,
    refetch: fetchDrawOffers
  };
};

export default useDrawOffers;